/**
 * Shared presentation helpers for the Search page and the ⌘K command palette.
 * Pure functions + a couple of tiny render helpers. No data fetching here.
 *
 * The search snippet from the API may arrive with inline highlight markup
 * (<mark>/<em>/<b>/<strong>). We NEVER inject it as raw HTML — instead we
 * tokenize it and rebuild React text nodes, so it is XSS-safe by construction.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { Browser, SearchHit } from '@vault/shared';
import { BROWSER_COLORS } from '@vault/ui';
import type { BrowserName } from '@vault/ui';

const KNOWN_BROWSERS: readonly BrowserName[] = ['chrome', 'edge', 'yandex', 'comet', 'firefox', 'safari'];

/** Colored identity dot for a browser; falls back to a muted dot for unknown. */
export function browserDotColor(b: Browser | null | undefined): string {
  if (b && (KNOWN_BROWSERS as readonly string[]).includes(b)) return BROWSER_COLORS[b as BrowserName];
  return 'var(--text-3)';
}

/** Uppercase browser label, or null when unknown/absent (so we can omit it). */
export function browserLabel(b: Browser | null | undefined): string | null {
  if (!b || b === 'unknown') return null;
  return b.toUpperCase();
}

/** Registrable-ish host, sans leading www. Robust against malformed urls. */
export function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').split('/')[0] ?? url;
  }
}

/** Deterministic 0..7 index into the favicon placeholder palette. */
export function faviconIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h % 8;
}

export function faviconLetter(seed: string): string {
  const c = seed.trim().charAt(0);
  return (c || '?').toUpperCase();
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/** "MAR 15" — mono meta voice. */
export function fmtDate(ms: number): string {
  const d = new Date(ms);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "09:41" 24h. */
export function fmtTime(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Turn matchedOn into a human reason label; `strong` gets the accent color. */
export function matchedReason(matchedOn: SearchHit['matchedOn']): { label: string; strong: boolean } | null {
  if (matchedOn.includes('content')) return { label: 'matched page content', strong: true };
  if (matchedOn.includes('semantic')) return { label: 'semantic match', strong: false };
  if (matchedOn.includes('title')) return { label: 'matched title', strong: false };
  if (matchedOn.includes('url')) return { label: 'matched url', strong: false };
  return null;
}

// ── safe highlighting ───────────────────────────────────────────────────────

export const HL_STYLE: CSSProperties = {
  background: 'var(--accent-tint)',
  color: 'var(--accent-ink)',
  borderRadius: 3,
  padding: '0 3px',
};

/** Strip any residual tags and decode the few entities a backend usually emits. */
function decodeText(s: string): string {
  return s
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Wrap query terms (len > 1) found in plain text with the accent mark. */
export function highlightTerms(text: string, query: string): ReactNode {
  const terms = query.trim().split(/\s+/).filter((t) => t.length > 1);
  if (terms.length === 0) return text;
  const pattern = terms.map(escapeRe).join('|');
  const splitRe = new RegExp(`(${pattern})`, 'gi');
  const testRe = new RegExp(`^(?:${pattern})$`, 'i');
  return text
    .split(splitRe)
    .filter((p) => p !== '')
    .map((p, i) =>
      testRe.test(p) ? (
        <mark key={i} style={HL_STYLE}>
          {p}
        </mark>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
}

const HAS_MARKUP = /<(?:mark|em|b|strong)>/i;

/**
 * Render an API snippet safely. If it carries highlight markup we rebuild the
 * highlighted segments; otherwise we fall back to client-side term highlighting.
 */
export function renderSnippet(raw: string, query: string): ReactNode {
  if (!HAS_MARKUP.test(raw)) return highlightTerms(decodeText(raw), query);

  const re = /<(?:mark|em|b|strong)>([\s\S]*?)<\/(?:mark|em|b|strong)>/gi;
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of raw.matchAll(re)) {
    const start = m.index ?? 0;
    if (start > last) out.push(<span key={key++}>{decodeText(raw.slice(last, start))}</span>);
    out.push(
      <mark key={key++} style={HL_STYLE}>
        {decodeText(m[1] ?? '')}
      </mark>,
    );
    last = start + m[0].length;
  }
  if (last < raw.length) out.push(<span key={key++}>{decodeText(raw.slice(last))}</span>);
  return out;
}
